import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/Jobs.css';

interface Job {
  _id: string;
  customer: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  driver?: {
    firstName: string;
    lastName: string;
    phone: string;
    vehiclePlate: string;
    rating: number;
  };
  pickupLocation: {
    address: string;
  };
  dropoffLocation: {
    address: string;
  };
  vehicleDetails: {
    type: string;
    plateNumber?: string;
  };
  serviceType: string;
  status: string;
  estimatedPrice: number;
  distance: number;
  createdAt: string;
}

interface JobOffer {
  _id: string;
  driver: {
    firstName: string;
    lastName: string;
    phone: string;
    rating: number;
    loyaltyPoints: number;
  };
  status: string;
  offeredAt: string;
  expiresAt: string;
  distanceToPickup: number;
  priority: number;
}

interface AvailableDriver {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  rating: number;
  loyaltyPoints: number;
  vehicleType: string;
  distance: number | null;
  distanceKm: string | null;
  hasLocation: boolean;
}

const Jobs: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status') || 'all';

  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [jobOffers, setJobOffers] = useState<JobOffer[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState<AvailableDriver[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [assigningDriver, setAssigningDriver] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, [statusFilter, serviceTypeFilter, page]);

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (serviceTypeFilter !== 'all') params.serviceType = serviceTypeFilter;

      const data = await api.getJobs(params);
      setJobs(data.jobs);
      setTotalPages(data.pagination.pages);
      setError('');
    } catch (err: any) {
      setError('Failed to load jobs');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchJobOffers = async (jobId: string) => {
    setLoadingOffers(true);
    try {
      const data = await api.getJobOffers(jobId);
      setJobOffers(data.offers);
    } catch (err: any) {
      console.error('Failed to load job offers:', err);
    } finally {
      setLoadingOffers(false);
    }
  };

  const handleViewOffers = (jobId: string) => {
    setSelectedJobId(jobId);
    fetchJobOffers(jobId);
  };

  const closeOffersModal = () => {
    setSelectedJobId(null);
    setJobOffers([]);
  };

  const fetchAvailableDrivers = async (jobId: string) => {
    setLoadingDrivers(true);
    try {
      const data = await api.getAvailableDriversForJob(jobId);
      setAvailableDrivers(data.availableDrivers);
    } catch (err: any) {
      console.error('Failed to load available drivers:', err);
      alert('Failed to load available drivers');
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleAssignDriver = async (jobId: string) => {
    setSelectedJobId(jobId);
    setShowAssignModal(true);
    await fetchAvailableDrivers(jobId);
  };

  const handleAssignConfirm = async (driverId: string) => {
    if (!selectedJobId) return;

    setAssigningDriver(true);
    try {
      await api.assignJobToDriver(selectedJobId, driverId);
      alert('Job offer sent to driver successfully!');
      setShowAssignModal(false);
      setSelectedJobId(null);
      await fetchJobs(); // Refresh jobs list
    } catch (err: any) {
      console.error('Failed to assign driver:', err);
      alert(err.response?.data?.error || 'Failed to assign driver');
    } finally {
      setAssigningDriver(false);
    }
  };

  const handleCancelJob = async (jobId: string) => {
    const reason = prompt('Enter cancellation reason (optional):');

    if (!window.confirm('Are you sure you want to cancel this job?')) {
      return;
    }

    try {
      await api.cancelJob(jobId, reason || undefined);
      alert('Job cancelled successfully');
      await fetchJobs(); // Refresh jobs list
    } catch (err: any) {
      console.error('Failed to cancel job:', err);
      alert(err.response?.data?.error || 'Failed to cancel job');
    }
  };

  const handleUnassignDriver = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to unassign the driver from this job? The job will return to pending status and can be reassigned.')) {
      return;
    }

    try {
      await api.unassignDriver(jobId);
      alert('Driver unassigned successfully. Job is now pending and can be reassigned.');
      await fetchJobs(); // Refresh jobs list
    } catch (err: any) {
      console.error('Failed to unassign driver:', err);
      alert(err.response?.data?.error || 'Failed to unassign driver');
    }
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedJobId(null);
    setAvailableDrivers([]);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'accepted': return 'status-accepted';
      case 'in_progress': return 'status-in-progress';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const getOfferStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'offer-pending';
      case 'accepted': return 'offer-accepted';
      case 'rejected': return 'offer-rejected';
      case 'expired': return 'offer-expired';
      case 'cancelled': return 'offer-cancelled';
      default: return '';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatServiceType = (serviceType: string) => {
    const serviceMap: { [key: string]: string } = {
      'towing': '🚚 Towing',
      'petrol_delivery': '⛽ Petrol Delivery',
      'jumpstart': '🔋 Jump Start',
      'tire_change': '🛞 Tire Change',
      'lockout_service': '🔑 Lockout Service',
      'winch_out': '⛓️ Winch Out',
    };
    return serviceMap[serviceType] || serviceType;
  };

  if (isLoading && jobs.length === 0) {
    return (
      <div className="jobs-page">
        <div className="loading">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="jobs-page">
      <div className="page-header">
        <div>
          <h1>Job Monitoring</h1>
          <p>Track all jobs and assignments</p>
        </div>
        <div className="header-actions">
          <button className="create-job-button" onClick={() => navigate('/jobs/create')}>
            ➕ Create Job
          </button>
          <button className="refresh-button" onClick={fetchJobs}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="all">All Jobs</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Service Type:</label>
          <select value={serviceTypeFilter} onChange={(e) => { setServiceTypeFilter(e.target.value); setPage(1); }}>
            <option value="all">All Services</option>
            <option value="towing">🚚 Towing</option>
            <option value="petrol_delivery">⛽ Petrol Delivery</option>
            <option value="jumpstart">🔋 Jump Start</option>
            <option value="tire_change">🛞 Tire Change</option>
            <option value="lockout_service">🔑 Lockout Service</option>
            <option value="winch_out">⛓️ Winch Out</option>
          </select>
        </div>

        <div className="results-count">
          {jobs.length} job{jobs.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Desktop Table */}
      <div className="desktop-table-container">
        <div className="table-container">
          <table className="jobs-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Service</th>
              <th>Customer</th>
              <th>Driver</th>
              <th>Pickup</th>
              <th>Dropoff</th>
              <th>Distance</th>
              <th>Price</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job._id}>
                <td>
                  <span className="job-id">{job._id.slice(-6)}</span>
                </td>
                <td>
                  <span className={`status-badge ${getStatusColor(job.status)}`}>
                    {job.status}
                  </span>
                </td>
                <td>
                  <span className="service-type">{formatServiceType(job.serviceType)}</span>
                </td>
                <td>
                  <div className="person-info">
                    <strong>{job.customer.firstName} {job.customer.lastName}</strong>
                    <span className="phone-number">{job.customer.phone}</span>
                  </div>
                </td>
                <td>
                  {job.driver ? (
                    <div className="person-info">
                      <strong>{job.driver.firstName} {job.driver.lastName}</strong>
                      <span className="phone-number">{job.driver.vehiclePlate}</span>
                    </div>
                  ) : (
                    <span className="no-driver">Not assigned</span>
                  )}
                </td>
                <td>
                  <span className="location-text">{job.pickupLocation.address}</span>
                </td>
                <td>
                  <span className="location-text">{job.dropoffLocation.address}</span>
                </td>
                <td>{job.distance.toFixed(1)} km</td>
                <td>₹{job.estimatedPrice}</td>
                <td>{formatDate(job.createdAt)}</td>
                <td>
                  <div className="action-buttons">
                    {(job.status === 'accepted' || job.status === 'in_progress' || job.status === 'completed') && (
                      <button
                        onClick={() => navigate(`/jobs/${job._id}/track`)}
                        className="btn-track-job"
                        title="Track job in real-time"
                      >
                        📍 Track
                      </button>
                    )}
                    <button
                      onClick={() => handleViewOffers(job._id)}
                      className="btn-view-offers"
                      title="View job offers"
                    >
                      👁️ Offers
                    </button>
                    {job.status === 'pending' && (
                      <button
                        onClick={() => handleAssignDriver(job._id)}
                        className="btn-assign-driver"
                        title="Assign driver manually"
                      >
                        👤 Assign
                      </button>
                    )}
                    {job.driver && (job.status === 'accepted' || job.status === 'in_progress') && (
                      <button
                        onClick={() => handleUnassignDriver(job._id)}
                        className="btn-unassign-driver"
                        title="Unassign driver and set job back to pending"
                      >
                        🔄 Unassign
                      </button>
                    )}
                    {(job.status === 'pending' || job.status === 'accepted') && (
                      <button
                        onClick={() => handleCancelJob(job._id)}
                        className="btn-cancel-job"
                        title="Cancel job"
                      >
                        ❌ Cancel
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

          {jobs.length === 0 && !isLoading && (
            <div className="empty-state">
              <p>No jobs found</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-card-list">
        {jobs.map((job) => (
          <div key={job._id} className="data-card">
            <div className="data-card-header">
              <div>
                <div className="data-card-title">
                  <span className="job-id">{job._id.slice(-6)}</span>
                  {' '}
                  <span className="service-type">{formatServiceType(job.serviceType)}</span>
                </div>
                <div className="data-card-subtitle">
                  {job.customer.firstName} {job.customer.lastName}
                </div>
              </div>
              <span className={`status-badge ${getStatusColor(job.status)}`}>
                {job.status}
              </span>
            </div>

            <div className="data-card-body">
              <div className="data-card-row">
                <span className="data-card-label">Customer:</span>
                <span className="data-card-value">{job.customer.phone}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Driver:</span>
                <span className="data-card-value">
                  {job.driver ?
                    `${job.driver.firstName} ${job.driver.lastName}` :
                    <span className="no-driver">Not assigned</span>
                  }
                </span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Pickup:</span>
                <span className="data-card-value location-text">{job.pickupLocation.address}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Dropoff:</span>
                <span className="data-card-value location-text">{job.dropoffLocation.address}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Distance:</span>
                <span className="data-card-value">{job.distance.toFixed(1)} km</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Price:</span>
                <span className="data-card-value"><strong>₹{job.estimatedPrice}</strong></span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Created:</span>
                <span className="data-card-value">{formatDate(job.createdAt)}</span>
              </div>
            </div>

            <div className="data-card-actions">
              {(job.status === 'accepted' || job.status === 'in_progress' || job.status === 'completed') && (
                <button
                  onClick={() => navigate(`/jobs/${job._id}/track`)}
                  className="btn-track-job"
                  title="Track job in real-time"
                >
                  📍 Track
                </button>
              )}
              <button
                onClick={() => handleViewOffers(job._id)}
                className="btn-view-offers"
                title="View job offers"
              >
                👁️ Offers
              </button>
              {job.status === 'pending' && (
                <button
                  onClick={() => handleAssignDriver(job._id)}
                  className="btn-assign-driver"
                  title="Assign driver manually"
                >
                  👤 Assign
                </button>
              )}
              {job.driver && (job.status === 'accepted' || job.status === 'in_progress') && (
                <button
                  onClick={() => handleUnassignDriver(job._id)}
                  className="btn-unassign-driver"
                  title="Unassign driver and set job back to pending"
                >
                  🔄 Unassign
                </button>
              )}
              {(job.status === 'pending' || job.status === 'accepted') && (
                <button
                  onClick={() => handleCancelJob(job._id)}
                  className="btn-cancel-job"
                  title="Cancel job"
                >
                  ❌ Cancel
                </button>
              )}
            </div>
          </div>
        ))}

        {jobs.length === 0 && !isLoading && (
          <div className="empty-state">
            <p>No jobs found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Job Offers Modal */}
      {selectedJobId && !showAssignModal && (
        <div className="modal-overlay" onClick={closeOffersModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Job Offers</h2>
              <button onClick={closeOffersModal} className="modal-close">✕</button>
            </div>

            {loadingOffers ? (
              <div className="modal-loading">Loading offers...</div>
            ) : jobOffers.length === 0 ? (
              <div className="modal-empty">No offers found for this job</div>
            ) : (
              <div className="offers-list">
                {jobOffers.map((offer) => (
                  <div key={offer._id} className="offer-card">
                    <div className="offer-header">
                      <div className="driver-info">
                        <strong>{offer.driver.firstName} {offer.driver.lastName}</strong>
                        <span className="driver-phone">{offer.driver.phone}</span>
                      </div>
                      <span className={`offer-status ${getOfferStatusColor(offer.status)}`}>
                        {offer.status}
                      </span>
                    </div>
                    <div className="offer-details">
                      <div className="detail-item">
                        <span className="label">Rating:</span>
                        <span className="value">⭐ {offer.driver.rating.toFixed(1)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Loyalty Points:</span>
                        <span className="value">{offer.driver.loyaltyPoints}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Distance:</span>
                        <span className="value">{(offer.distanceToPickup / 1000).toFixed(1)} km</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Priority:</span>
                        <span className="value">{offer.priority.toFixed(2)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Offered:</span>
                        <span className="value">{formatDate(offer.offeredAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign Driver Modal */}
      {showAssignModal && selectedJobId && (
        <div className="modal-overlay" onClick={closeAssignModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Driver to Job</h2>
              <button onClick={closeAssignModal} className="modal-close">✕</button>
            </div>

            {loadingDrivers ? (
              <div className="modal-loading">Loading available drivers...</div>
            ) : availableDrivers.length === 0 ? (
              <div className="modal-empty">
                <p>No available drivers found</p>
                <p className="hint">Make sure drivers are online and have location enabled</p>
              </div>
            ) : (
              <div className="drivers-list">
                <p className="info-text">
                  {availableDrivers.length} driver{availableDrivers.length !== 1 ? 's' : ''} available
                </p>
                {availableDrivers.map((driver) => (
                  <div key={driver.id} className="driver-card">
                    <div className="driver-header">
                      <div className="driver-info">
                        <strong>{driver.firstName} {driver.lastName}</strong>
                        <span className="driver-phone">{driver.phone}</span>
                      </div>
                      <button
                        onClick={() => handleAssignConfirm(driver.id)}
                        className="btn-assign-confirm"
                        disabled={assigningDriver}
                      >
                        {assigningDriver ? 'Assigning...' : 'Assign'}
                      </button>
                    </div>
                    <div className="driver-details">
                      <div className="detail-item">
                        <span className="label">Vehicle:</span>
                        <span className="value">{driver.vehicleType}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Rating:</span>
                        <span className="value">⭐ {driver.rating.toFixed(1)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Loyalty:</span>
                        <span className="value">{driver.loyaltyPoints} pts</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Distance:</span>
                        <span className="value">
                          {driver.hasLocation ?
                            `${driver.distanceKm} km` :
                            'Location unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
